import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { featuredServices } from '../../mocks/services';
import { testimonialsData } from '../../mocks/testimonials';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full">
                <i className="ri-vr-line text-primary"></i>
                <span className="text-sm font-medium text-primary">Technologie VR disponible</span>
              </div>

              <h1 className="font-serif text-5xl lg:text-7xl font-bold text-gray-900 leading-tight">
                Bien-être & Vitalité
                <br />
                <span className="text-primary">mentale, émotionnelle et physique</span>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed">
                HMC CLINIC vous accompagne vers une vie plus saine et équilibrée à Lubumbashi. 
                Découvrez nos services professionnels de bien-être adaptés à vos besoins.
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="https://wa.me/243850465500"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-3 bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-semibold transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-2xl"></i>
                  <span>Prendre rendez-vous</span>
                </a>
                <Link
                  to="/nos-services"
                  className="inline-flex items-center space-x-3 bg-white hover:bg-gray-50 text-primary border-2 border-primary px-8 py-4 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap"
                >
                  <span>Découvrir nos services</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                <div>
                  <div className="text-3xl font-bold text-primary">500+</div>
                  <div className="text-sm text-gray-600">Patients satisfaits</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary">7</div>
                  <div className="text-sm text-gray-600">Services spécialisés</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary">5★</div>
                  <div className="text-sm text-gray-600">Note moyenne</div>
                </div>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-l-3xl overflow-hidden shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-b from-primary/20 via-transparent to-primary/20"></div>
                <img
                  src="https://static.readdy.ai/image/a0897f463ac1d23797a54fc0386c922a/a0e89acb2706aaf338a07a2f5f52c9ae.jpeg"
                  alt="HMC CLINIC Lubumbashi"
                  className="w-full h-[600px] object-cover object-top"
                />
              </div>
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-accent/10 rounded-full">
                    <i className="ri-map-pin-line text-2xl text-accent"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Lubumbashi</div>
                    <div className="text-sm text-gray-600">Haut-Katanga, RDC</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <span className="text-sm font-medium text-primary">Nos Services</span>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Services professionnels
              <br />
              pour votre bien-être
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/service/${service.slug}`}
                  className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
                >
                  <div className="relative h-64 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent z-10"></div>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg z-20">
                      <i className={`${service.icon} text-2xl text-primary`}></i>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-semibold text-xl text-gray-900 mb-3 group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {service.shortDescription}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-primary">{service.duration}</span>
                      <div className="w-10 h-10 flex items-center justify-center bg-primary rounded-full group-hover:bg-accent transition-colors">
                        <i className="ri-arrow-right-line text-white"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link
              to="/nos-services"
              className="inline-flex items-center space-x-2 bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap"
            >
              <span>Voir tous les services</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Ce que disent nos patients
            </h2>
            <p className="text-lg text-gray-600">
              Découvrez les témoignages de ceux qui nous font confiance
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonialsData.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-primary/10 rounded-full text-primary font-semibold text-lg">
                    {testimonial.initials}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600 flex items-center">
                      <i className="ri-map-pin-line text-xs mr-1"></i>
                      {testimonial.location}
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  « {testimonial.text} »
                </p>
                <div className="flex space-x-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400"></i>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Contact Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-3xl overflow-hidden shadow-2xl h-[500px]"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3933.123456789!2d27.4667!3d-11.6667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTHCsDQwJzAwLjEiUyAyN8KwMjgnMDAuMSJF!5e0!3m2!1sfr!2scd!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="HMC CLINIC Location"
              ></iframe>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  Nous trouver
                </h2>
                <p className="text-lg text-gray-600">
                  Visitez-nous à notre clinique moderne au cœur de Lubumbashi
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-full flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-primary"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Adresse</div>
                    <div className="text-gray-600">
                      Av. de la Révolution N°2058
                      <br />
                      Lubumbashi, Haut-Katanga
                      <br />
                      République Démocratique du Congo
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#25D366]/10 rounded-full flex-shrink-0">
                    <i className="ri-whatsapp-line text-xl text-[#25D366]"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">WhatsApp</div>
                    <a
                      href="https://wa.me/243850465500"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#25D366] hover:underline cursor-pointer"
                    >
                      +243 850 465 500
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-full flex-shrink-0">
                    <i className="ri-mail-line text-xl text-primary"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Email</div>
                    <a
                      href="mailto:hmcclinicrdc@gmail.com"
                      className="text-primary hover:underline cursor-pointer"
                    >
                      hmcclinicrdc@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              <a
                href="https://wa.me/243850465500"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-3 bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-semibold transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                <span>Prendre rendez-vous maintenant</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;